import {
	AccordionDetails,
	AccordionSummary,
	Divider,
	Stack,
	Typography,
	useTheme,
} from '@mui/material';
import React from 'react';

import {StyledCard, TagChip} from 'presentation/components';
import Icon from 'presentation/icons';
import IconNamesType from 'presentation/icons/IconNamesType';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {ProtocolStatusEnum} from 'presentation/pages/private/consult-protocols/constants';
import {formatProtocolDate} from '../../../utils/formatProtocolDate';
import {StyledAccordion} from '../../styles';
import {ProtocolAccordionProps} from './types';
import {extractProtocolInfo} from '../../../utils/extractProtocolInfo';

const {descriptionLabel, details} = translations['pt-br'].commons;
const {protocolLabel, requestDateLabel} = translations['pt-br'].privateFlow.consultProtocols;

export function ProtocolAccordion({protocol}: ProtocolAccordionProps) {
	const theme = useTheme();
	const {isMobile, isTablet} = useMediaQueries();
	const {protocolSummaryList, shouldShowDescription, protocolDetailsList} =
		extractProtocolInfo(protocol);

	const isStatusCompleted = protocol.statusDescricao === ProtocolStatusEnum.COMPLETED;

	return (
		<StyledAccordion>
			<AccordionSummary
				expandIcon={
					<Icon name="expand-more-filled" color={theme.palette.primary.main} size={20} />
				}
				aria-controls="panel1-content"
				id="panel1-header">
				<Stack
					sx={{
						flexDirection: isTablet ? 'column' : 'row',
						alignItems: isTablet ? 'initial' : 'center',
						gap: 0.5,
						flexWrap: 'wrap',
					}}>
					<Typography variant="body1" color="text.primary">
						{protocolLabel} <b>{protocol?.registro}</b>
					</Typography>

					{!isTablet ? (
						<Typography variant="body2" color="text.secondary">
							{!isTablet ? '|' : null} {requestDateLabel}
							{formatProtocolDate(protocol?.dtAtendimento)}
						</Typography>
					) : null}
					<Typography variant="body2" color="text.secondary">
						{!isTablet ? '|' : null} {protocol?.tipoProtocolo}
					</Typography>
				</Stack>
				{isTablet ? (
					<StyledCard.Content
						sx={{flexDirection: isMobile ? 'column' : 'row'}}
						list={protocolSummaryList}
					/>
				) : null}

				<Stack
					flexDirection="row"
					justifyContent={isTablet ? 'space-between' : 'baseline'}
					alignItems="center"
					gap={3}>
					{protocol?.statusDescricao ? (
						<TagChip
							label={protocol.statusDescricao}
							color={isStatusCompleted ? 'success' : 'primary'}
						/>
					) : null}

					<Typography variant="subtitle2" color={theme.palette.primary.main}>
						{details}
					</Typography>
				</Stack>
			</AccordionSummary>
			<Divider sx={{my: 2}} />
			<AccordionDetails sx={{px: 0}}>
				<StyledCard.Content
					sx={{flexDirection: isMobile ? 'column' : 'row'}}
					list={protocolDetailsList}
				/>
				{shouldShowDescription ? (
					<Stack mt={2} gap={1}>
						<StyledCard.Content
							sx={{flexDirection: isMobile ? 'column' : 'row'}}
							list={[
								{
									iconName: 'notes-filled' as IconNamesType,
									primaryText: String(protocol.observacao),
									secondaryText: descriptionLabel,
									secondaryTextColor: 'text.secondary',
									flexDirection: 'column-reverse',
								},
							]}
						/>
					</Stack>
				) : null}
			</AccordionDetails>
		</StyledAccordion>
	);
}
